// src/components/services/Parcours.jsx
import * as React from 'react'
import { Container, Box, Typography, Button } from '@mui/material'

import camion from '../../assets/icones/icone-camion.png'
import controle from '../../assets/icones/icone-controle.png'
import cursor from '../../assets/icones/icone-cursor.png'
import encaisser from '../../assets/icones/icone-encaisser.png'
import valide from '../../assets/icones/icone-valide.png'

// i18n
import { useI18n } from '../../i18n/I18nProvider'
import parcoursI18n from '../../i18n/components/services/parcours.i18n'

export default function Parcours() {
  const { t, register } = useI18n()
  React.useEffect(() => { register('services', parcoursI18n) }, [register])

  // steps depuis l’i18n (on garde les mêmes icônes et href)
  const steps = React.useMemo(() => ([
    {
      id: 1, side: 'left',  icon: cursor,
      title: t('services.parcours.steps.1.title'),
      body:  t('services.parcours.steps.1.body'),
      cta:   { label: t('services.parcours.steps.1.cta'), href: '/tarifs' },
    },
    {
      id: 2, side: 'right', icon: valide,
      title: t('services.parcours.steps.2.title'),
      body:  t('services.parcours.steps.2.body'),
    },
    {
      id: 3, side: 'left',  icon: camion,
      title: t('services.parcours.steps.3.title'),
      body:  t('services.parcours.steps.3.body'),
    },
    {
      id: 4, side: 'right', icon: controle,
      title: t('services.parcours.steps.4.title'),
      body:  t('services.parcours.steps.4.body'),
    },
    {
      id: 5, side: 'left',  icon: encaisser,
      title: t('services.parcours.steps.5.title'),
      body:  t('services.parcours.steps.5.body'),
    },
  ]), [t])

  return (
    <Box sx={{ bgcolor: '#fff' }}>
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 4 } }}>
        <Typography
          component="h2"
          sx={{ fontWeight: 900, fontSize: { xs: 28, md: 34 }, mb: { xs: 3.5, md: 5 } }}
        >
          {t('services.parcours.title')}
        </Typography>

        {/* === Desktop / Tablet (md+) === */}
        <Box
          sx={{
            display: { xs: 'none', md: 'grid' },
            gridTemplateColumns: '1fr 120px 1fr',
            rowGap: 6,
            columnGap: { md: 4, lg: 5 },
            position: 'relative',
            alignItems: 'center',
          }}
        >
          {/* Ligne pointillée */}
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              borderLeft: '3px dashed #0466c8',
              opacity: 0.6,
              zIndex: 0,
            }}
          />

          {steps.map((s) => (
            <React.Fragment key={s.id}>
              <Box sx={{ gridColumn: 1, alignSelf: 'center' }}>
                {s.side === 'left' && <StepBlock step={s} />}
              </Box>

              <Box
                sx={{
                  gridColumn: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  alignSelf: 'center',
                }}
              >
                <CircleIndex index={s.id} />
              </Box>

              <Box sx={{ gridColumn: 3, alignSelf: 'center' }}>
                {s.side === 'right' && <StepBlock step={s} />}
              </Box>
            </React.Fragment>
          ))}
        </Box>

        {/* === Mobile (xs/sm) === */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 4 }}>
          {steps.map((s) => (
            <Box
              key={s.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: '50px 50px 1fr', // numéro + icône + texte
                gap: 2,
                alignItems: 'center',
              }}
            >
              {/* Numéro */}
              <CircleIndex index={s.id} size={48} />

              {/* Icône */}
              <Box
                component="img"
                src={s.icon}
                alt=""
                sx={{ width: 40, height: 40, objectFit: 'contain', opacity: 0.95 }}
              />

              {/* Texte */}
              <StepTexts step={s} />
            </Box>
          ))}
        </Box>

        {/* Bas de section */}
        <Box sx={{ mt: { xs: 6, md: 8 } }}>
          <Typography
            sx={{ color: 'text.secondary', fontSize: { xs: 14.5, sm: 15, md: 16 }, lineHeight: 1.7, mb: 2 }}
          >
            {t('services.parcours.bottom.p1.s1')}
            <b>{t('services.parcours.bottom.p1.bold1')}</b>
            {t('services.parcours.bottom.p1.s2')}
            <b>{t('services.parcours.bottom.p1.bold2')}</b>
            {t('services.parcours.bottom.p1.s3')}
            <b>{t('services.parcours.bottom.p1.bold3')}</b>
            {t('services.parcours.bottom.p1.s4')}
          </Typography>

          <Typography
            sx={{ color: 'text.secondary', fontSize: { xs: 14.5, sm: 15, md: 16 }, lineHeight: 1.7 }}
          >
            {t('services.parcours.bottom.p2.s1')}
            <b>{t('services.parcours.bottom.p2.bold1')}</b>
            {t('services.parcours.bottom.p2.s2')}
            <b>{t('services.parcours.bottom.p2.bold2')}</b>
            {t('services.parcours.bottom.p2.s3')}
            <b>{t('services.parcours.bottom.p2.bold3')}</b>
            {t('services.parcours.bottom.p2.s4')}
          </Typography>
        </Box>
      </Container>
    </Box>
  )
}

/* ===== Sous-composants ===== */

function CircleIndex({ index, size = 66 }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: '#0466c8',
        color: '#fff',
        fontWeight: 900,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size < 60 ? 18 : 22,
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        zIndex: 1,
      }}
    >
      {index}
    </Box>
  )
}

function StepBlock({ step }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.75, maxWidth: 620 }}>
      <Box
        component="img"
        src={step.icon}
        alt=""
        sx={{ width: 56, height: 56, objectFit: 'contain', opacity: 0.95, flexShrink: 0 }}
      />
      <StepTexts step={step} />
    </Box>
  )
}

function StepTexts({ step }) {
  return (
    <Box sx={{ textAlign: 'left' }}>
      <Typography sx={{ fontWeight: 800, fontSize: { xs: 17, md: 20 }, mb: 0.5, lineHeight: 1.25 }}>
        {step.title}
      </Typography>
      <Typography sx={{ color: 'text.secondary', fontSize: { xs: 15, md: 16.5 }, lineHeight: 1.65, mb: step.cta ? 1.5 : 0 }}>
        {step.body}
      </Typography>
      {step.cta && (
        <Button
          href={step.cta.href}
          size="small"
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 2,
            backgroundColor: '#0466c8',
            '&:hover': { backgroundColor: '#004890ff' },
          }}
        >
          {step.cta.label}
        </Button>
      )}
    </Box>
  )
}